// Utilities
import WindowMetrics from '../utilities/window-metrics';
import {
    parsedQueryString,
    screenshotModeEnabled,
    trimValue,
} from '../utilities/utilities';

// Components
import ScrollLockAnimation from './scroll-lock-animation';
import ResponsiveVideoLoader from './responsive-video-loader';
import CircleScreensBackground from './circle-screens-background';
import CircleScreen from './circle-screen';

const settings = {
    test: {
        enabled: false,

        // Plays through the timeline
        play: false,
        timeScale: 2,

        // If play is set to false, this will jump to a specific screen
        skipToSlide: 0,
    },

    dataAttributes: {
        screenType: 'data-screen-type',
        floatingEndContentIdentifier: 'data-video-circle-screens-scroll-end-content',
    },

    cssProperties: {
        headerOpacity: '--header-opacity',
    },

    classNames: {
        container: 'video-circle-screens-scroll',
    },

    selectors: {
        scrollLockContainer: '.video-circle-screens-scroll__scroll-lock',
        video: {
            desktop: 'video.desktop-only',
            mobile: 'video.mobile-only',
            anyVideo: 'video',
        },
        header: 'h2',
        screenGroupContainers: 'div[class^="circle-screens__group-"]',
        circleScreensBackground: '.circle-screen-background',
        screenContainers: 'div[data-screen-type]',
        floatingEndContentWrapper: '.floating-midsection-content',
    },

    screenshotModeParameters: {
        screenIndex: 'video-index',
    },

    animation: {
        scrollDistancePerSecond: 600,
    },
};

const screenTypes = {
    group1Start: 1,
    group2Start: 2,
    group3Start: 3,
    group3Middle: 4,
    group3End: 5,
};

export default class VideoCircleScreensScroll extends ScrollLockAnimation {
    constructor(container) {
        // Find our scroll lock container
        const scrollLockContainer = container
            .querySelector(settings.selectors.scrollLockContainer);

        // Create timeline
        const timeline = gsap.timeline({
            paused: true,
        });

        // At the start of the animation, fade out the header (mobile only)
        const header = container
            .querySelector(settings.selectors.header);
        if (header) {
            const progress = {
                percentComplete: 0,
            };

            timeline.to(
                progress,
                1.0,
                {
                    ease: 'power2.out',
                    percentComplete: 1,
                    onUpdate: () => {
                        header.style.setProperty(
                            settings.cssProperties.headerOpacity,
                            trimValue(1 - progress.percentComplete),
                        );
                    },
                },
                0,
            );
        }

        // Find our screen groups, backgrounds, and screens,
        // and add their animations to the timeline
        const screens = [];
        const screenGroupContainers = container
            .querySelectorAll(settings.selectors.screenGroupContainers);
        for (let i=0; i<screenGroupContainers.length; i++) {
            const screenGroupContainer = screenGroupContainers[i];

            // Find the circle background element for this group
            const background = new CircleScreensBackground(
                screenGroupContainer.querySelector(
                    settings.selectors.circleScreensBackground,
                ),
            );

            const screenContainers = screenGroupContainer
                .querySelectorAll(settings.selectors.screenContainers);
            for (let i=0; i<screenContainers.length; i++) {
                const screen = new Screen(
                    screenContainers[i],
                    background,
                    timeline,
                );

                screens.push(screen);
            }
        }

        // At the end of the animation, show the floating midsection content
        const floatingEndContentIdentifier = container.getAttribute(
            settings.dataAttributes.floatingEndContentIdentifier,
        );

        if (floatingEndContentIdentifier) {
            const floatingEndContentWrapper = document.querySelector(
                `${settings.selectors.floatingEndContentWrapper}[${settings.dataAttributes.floatingEndContentIdentifier} = '${floatingEndContentIdentifier}']`,
            );

            if (floatingEndContentWrapper) {
                timeline.to(
                    floatingEndContentWrapper,
                    0.5,
                    {
                        ease: 'none',
                        opacity: 1,
                        pointerEvents: 'all',
                    },
                    '-=0.25',
                );
            }
        }

        // Determine the amount of scroll distance in which we'll transition between
        // the timestamp at which the first screen is fully visible and the end of
        // the animation - first, determine the duration between these two points in
        // the timeline, in seconds
        let scrollLockDistance = timeline.duration();

        // Next, convert seconds to pixels
        scrollLockDistance *= settings.animation.scrollDistancePerSecond;
        scrollLockDistance = Math.round(scrollLockDistance);

        super(
            scrollLockContainer,
            scrollLockDistance,
        );

        // Store these now that "this" has been established
        this.screens = screens;
        this.timeline = timeline;

        this.videos = {
            mobile: null,
            desktop: null,
            anyVideo: null,
        };

        // Set up a callback looking for video elements added dynamically
        // Note: this "addCreateVideoCallback" functionality may not be
        // needed if we just end up using one video for mobile & desktop -
        // the ResponsiveVideoLoader class is in place to avoid both videos
        // loading with the page, which happens if all you do is hide them
        // via CSS
        const createVideoCallback = () => {
            if (!this.videos.mobile) {
                this.videos.mobile = this.container
                    .querySelector(settings.selectors.video.mobile);

                // These videos don't appear to load on iOS until they start playing,
                // so we have them set to autoplay on page load and then stop them here
                if (this.videos.mobile) {
                    const stopPlayback = () => {
                        this.videos.mobile.pause();
                        this.videos.mobile.currentTime = 0;
                    };

                    this.videos.mobile.addEventListener('play', stopPlayback);
                    stopPlayback();
                }
            }

            if (!this.videos.desktop) {
                this.videos.desktop = this.container
                    .querySelector(settings.selectors.video.desktop);

                if (this.videos.desktop) {
                    const stopPlayback = () => {
                        this.videos.desktop.pause();
                        this.videos.desktop.currentTime = 0;
                    };

                    this.videos.desktop.addEventListener('play', stopPlayback);
                    stopPlayback();
                }
            }

            if (!this.videos.mobile && !this.videos.desktop) {
                this.videos.anyVideo = this.container
                    .querySelector(settings.selectors.video.anyVideo);

                if (this.videos.anyVideo) {
                    const stopPlayback = () => {
                        this.videos.anyVideo.pause();
                        this.videos.anyVideo.currentTime = 0;
                    };

                    this.videos.anyVideo.addEventListener('play', stopPlayback);
                    stopPlayback();
                }
            }
        };

        ResponsiveVideoLoader.addCreateVideoCallback(createVideoCallback);
        createVideoCallback();

        // Testing
        if (settings.test.enabled) {
            if (settings.test.play) {
                this.timeline.timeScale(settings.test.timeScale);
                this.timeline.play();
            } else {
                this.timeline.seek(
                    this.screens[settings.test.skipToSlide]
                        .timestamps
                        .visible);
            }

            // Stop here
            return;
        }

        // Check if screenshot mode is enabled
        if (screenshotModeEnabled) {
            // Check if a screen index was provided
            let screenIndex = parsedQueryString[
                settings
                    .screenshotModeParameters
                    .screenIndex
            ];

            screenIndex = parseInt(screenIndex);
            if (
                !isNaN(screenIndex) &&
                screenIndex >= 0 &&
                screenIndex < this.screens.length
            ) {
                // Translate the timestamp associated with the screen
                // at the specified index to a 0-1 progress value
                const progress =
                    (
                        this.screens[screenIndex]
                            .timestamps
                            .visible
                    ) /
                    this.timeline.duration();

                // Move the animation to the calculated progress value
                this.setAnimationProgress(progress);
            }
        }
    }

    static init() {
        // Find elements with the expected class name
        const elements = document
            .getElementsByClassName(settings.classNames.container);

        for (let i=0; i<elements.length; i++) {
            new VideoCircleScreensScroll(elements[i]);
        }
    }

    /**
     * Signals that we should update our position within the animation;
     * "progress" indicates how far the user has scrolled through
     * the animation when the container is fully onscreen;
     * See the base class for details
     * @param {number} progress
     */
    setAnimationProgress(progress) {
        // Call base class
        super.setAnimationProgress(progress);

        // Update position in the timeline
        this.timeline.progress(progress);

        // Determine what video to scrub
        let video;
        if (WindowMetrics.isMobile()) {
            video = this.videos.mobile;
            if (!video) {
                video = this.videos.anyVideo;
            }
        } else {
            video = this.videos.desktop;
            if (!video) {
                video = this.videos.anyVideo;
            }
        }

        if (!video) {
            return;
        }

        if (isNaN(video.duration)) {
            return;
        }

        // For some reason on iOS the video disappears if you
        // seek near it's actual duration
        const duration = video.duration * 0.99;

        // Scrub the video
        const scrubTime = Math.floor(
            duration *
            progress *
            10000,
        );

        video.currentTime = scrubTime / 10000;
    }
}

/*
    Internal classes and functions
*/

class Screen extends CircleScreen {
    /**
     * @constructor
     * @param {HTMLElement} container
     * @param {CircleScreensBackground} background
     * @param {object} timeline
     */
    constructor(
        container,
        background,
        timeline,
    ) {
        // Call base class
        super(container);

        // Determine screen type
        this.screenType = container
            .getAttribute(settings.dataAttributes.screenType);
        switch (this.screenType) {
            case 'group-1-start':
                this.screenType = screenTypes.group1Start;
                break;

            case 'group-2-start':
                this.screenType = screenTypes.group2Start;
                break;

            case 'group-3-start':
                this.screenType = screenTypes.group3Start;
                break;

            case 'group-3-end':
                this.screenType = screenTypes.group3End;
                break;

            default:
                if (this.screenType.startsWith('group-3-mid-')) {
                    this.screenType = screenTypes.group3Middle;
                } else {
                    throw new Error(`Unrecognized screen type: ${this.screenType}`);
                }
                break;
        }

        // Colored circles - these arrays define the order
        // in which the circles appear
        const smallCircleTypes = [
            CircleScreensBackground.elementTypes.blueCircle,
            CircleScreensBackground.elementTypes.yellowCircle,
            CircleScreensBackground.elementTypes.redCircle,
        ];

        // Some slides define an offset for the smaller circles
        const smallCircleOffsets = [
            this.parameters.blueOffset,
            this.parameters.yellowOffset,
            this.parameters.redOffset,
        ];

        // Record start timestamp
        this.timestamps = {
            start: timeline.duration(),
        };

        // Add animation to timeline
        switch (this.screenType) {
            case screenTypes.group1Start:
                {
                    // Background transition (first appearance)
                    // Note we're moving backward to overlap with
                    // the fade-out of header (on mobile - on desktop it's just not apparent)
                    const backgroundStartTime = this.timestamps.start - 1.0;
                    background.setOpacity(
                        CircleScreensBackground.elementTypes.whiteCircle,
                        1,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.out');

                    background.transitionScale(
                        this.parameters.bgScaleVisible,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.out');

                    // Show the circles
                    smallCircleTypes.forEach((smallCircleType, index) => {
                        // Delay between circles
                        const circleStartTime = backgroundStartTime +
                            0.5 +
                            (index * 0.25);

                        background.transitionSmallCircle(
                            smallCircleType,
                            1,
                            smallCircleOffsets[index],
                            timeline,
                            circleStartTime,
                            1.5,
                            'power1.out',
                        );
                    });

                    // Foreground transition
                    const foregroundStartTime = this.timestamps.start - 0.5;
                    this.show(
                        timeline,
                        foregroundStartTime + 0.25);

                    // Record visible timestamp
                    this.timestamps.visible = timeline.duration();

                    // Background hide
                    const hideStartTime = this.timestamps.visible;
                    background.setOpacity(
                        CircleScreensBackground.elementTypes.whiteCircle,
                        0,
                        timeline,
                        hideStartTime,
                        1.0,
                        'power1.out');

                    background.transitionScale(
                        this.parameters.bgScaleHide,
                        timeline,
                        hideStartTime,
                        1.0,
                        'power1.out');

                    // Hide the circles
                    smallCircleTypes.forEach((smallCircleType, index) => {
                        background.transitionSmallCircle(
                            smallCircleType,
                            0,
                            null,
                            timeline,
                            hideStartTime,
                            1.0,
                            'power1.out',
                        );
                    });

                    // Foreground hide
                    this.hide(
                        timeline,
                        hideStartTime);
                }
                break;

            case screenTypes.group2Start:
                {
                    // Background transition (first appearance)
                    // Note we're moving backward to overlap with
                    // the fade-out of the previous slide
                    const backgroundStartTime = this.timestamps.start - 1.0;
                    background.setOpacity(
                        CircleScreensBackground.elementTypes.whiteCircle,
                        1,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.out');

                    background.transitionScale(
                        this.parameters.bgScaleVisible,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.inOut');

                    // Show the circles
                    smallCircleTypes.forEach((smallCircleType, index) => {
                        // Delay between circles
                        const circleStartTime = backgroundStartTime +
                            0.25 +
                            (index * 0.25);

                        background.transitionSmallCircle(
                            smallCircleType,
                            1,
                            smallCircleOffsets[index],
                            timeline,
                            circleStartTime,
                            1.5,
                            'power1.out',
                        );
                    });

                    // Foreground transition
                    const foregroundStartTime = this.timestamps.start - 0.5;
                    this.show(
                        timeline,
                        foregroundStartTime);

                    // Record visible timestamp
                    this.timestamps.visible = timeline.duration();

                    // Background hide
                    const hideStartTime = this.timestamps.visible;
                    background.setOpacity(
                        CircleScreensBackground.elementTypes.whiteCircle,
                        0,
                        timeline,
                        hideStartTime,
                        1.0,
                        'power1.out');

                    background.transitionScale(
                        this.parameters.bgScaleHide,
                        timeline,
                        hideStartTime,
                        1.0,
                        'power1.out');

                    // Hide the circles
                    smallCircleTypes.forEach((smallCircleType, index) => {
                        background.transitionSmallCircle(
                            smallCircleType,
                            0,
                            null,
                            timeline,
                            hideStartTime,
                            1.0,
                            'power1.out',
                        );
                    });

                    // Foreground hide
                    this.hide(
                        timeline,
                        hideStartTime);
                }
                break;

            case screenTypes.group3Start:
                {
                    // Background transition (first appearance)
                    // Note we're moving backward to overlap with
                    // the fade-out of the previous slide
                    const backgroundStartTime = this.timestamps.start - 1.0;
                    background.setOpacity(
                        CircleScreensBackground.elementTypes.whiteCircle,
                        1,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.out');

                    background.transitionScale(
                        this.parameters.bgScaleVisible,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.inOut');

                    // Foreground transition
                    const foregroundStartTime = this.timestamps.start - 0.5;
                    this.show(
                        timeline,
                        foregroundStartTime);

                    // Record visible timestamp
                    this.timestamps.visible = timeline.duration();

                    // Foreground hide
                    const hideStartTime = this.timestamps.visible;
                    this.hide(
                        timeline,
                        hideStartTime);
                }
                break;

            case screenTypes.group3Middle:
                {
                    // Background transition (changing sizes)
                    // Note we're moving backward to overlap with
                    // the fade-out of the previous slide
                    const backgroundStartTime = this.timestamps.start - 0.5;
                    background.transitionScale(
                        this.parameters.bgScaleVisible,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.inOut');

                    // Foreground transition
                    const foregroundStartTime = this.timestamps.start;
                    this.show(
                        timeline,
                        foregroundStartTime);

                    // Record visible timestamp
                    this.timestamps.visible = timeline.duration();

                    // Foreground hide
                    const hideStartTime = this.timestamps.visible;
                    this.hide(
                        timeline,
                        hideStartTime);
                }
                break;

            case screenTypes.group3End:
                {
                    // Background transition (changing sizes + introducing colored circles)
                    // Note we're moving backward to overlap with
                    // the fade-out of the previous slide
                    const backgroundStartTime = this.timestamps.start - 0.5;
                    background.transitionScale(
                        this.parameters.bgScaleVisible,
                        timeline,
                        backgroundStartTime,
                        1.0,
                        'power1.inOut');

                    // Show the circles
                    smallCircleTypes.forEach((smallCircleType, index) => {
                        // Delay between circles
                        const circleStartTime = backgroundStartTime +
                            0.25 +
                            (index * 0.25);

                        background.transitionSmallCircle(
                            smallCircleType,
                            1,
                            smallCircleOffsets[index],
                            timeline,
                            circleStartTime,
                            1.5,
                            'power1.out',
                        );
                    });

                    // Foreground transition
                    const foregroundStartTime = this.timestamps.start;
                    this.show(
                        timeline,
                        foregroundStartTime);

                    // Record visible timestamp
                    this.timestamps.visible = timeline.duration();
                }
                break;
        }

        // Record end timestamp
        this.timestamps.end = timeline.duration();
    }
}
